import { useState, useEffect, useCallback } from 'react';
import type { MonetizationTimePeriod } from '@/mocks/monetizationAnalytics';
import { getMonetizationAnalytics, revenueKpis, performanceKpis, monetizationTimePeriodLabels } from '@/mocks/monetizationAnalytics';
import MonetizationKpiCard from '@/pages/admin/monetization-analytics/components/MonetizationKpiCard';
import MonetizationTopsSection from '@/pages/admin/monetization-analytics/components/MonetizationTopsSection';

type LoadState = 'loading' | 'ready' | 'error' | 'empty';

export default function RevenueTabContent() {
  const [dataState, setDataState] = useState<LoadState>('loading');
  const [analyticsData, setAnalyticsData] = useState<ReturnType<typeof getMonetizationAnalytics> | null>(null);
  const [timePeriod, setTimePeriod] = useState<MonetizationTimePeriod>('mes');
  const [activeTab, setActiveTab] = useState<string>('campanas');

  const loadData = useCallback(() => {
    setDataState('loading');
    setTimeout(() => {
      const shouldError = Math.random() < 0.06;
      const shouldBeEmpty = Math.random() < 0.03;
      if (shouldError) {
        setDataState('error');
        setAnalyticsData(null);
      } else if (shouldBeEmpty) {
        setDataState('empty');
        setAnalyticsData(null);
      } else {
        const data = getMonetizationAnalytics();
        setAnalyticsData(data);
        setDataState('ready');
      }
    }, 700);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const retry = () => loadData();

  const presetLabel = monetizationTimePeriodLabels[timePeriod];
  const totalRevenue = revenueKpis.reduce((sum, kpi) => sum + kpi[timePeriod].value, 0);

  /* ─── LOADING ─── */
  if (dataState === 'loading') {
    return (
      <div className="space-y-5 animate-pulse">
        <div className="bg-background-50 rounded-2xl border border-background-200 p-6">
          <div className="w-48 h-5 rounded-md bg-background-200 mb-3"></div>
          <div className="w-72 h-3 rounded bg-background-200"></div>
          <div className="flex gap-2 mt-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-20 h-9 rounded-full bg-background-200"></div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-16 h-3 rounded bg-background-200"></div>
                <div className="w-8 h-8 rounded-lg bg-background-200"></div>
              </div>
              <div className="w-20 h-7 rounded-md bg-background-200 mb-2"></div>
              <div className="w-24 h-3 rounded bg-background-200"></div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-background-50 rounded-xl border border-background-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="w-12 h-3 rounded bg-background-200"></div>
                <div className="w-8 h-8 rounded-lg bg-background-200"></div>
              </div>
              <div className="w-16 h-7 rounded-md bg-background-200 mb-2"></div>
              <div className="w-20 h-3 rounded bg-background-200"></div>
            </div>
          ))}
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-5">
          <div className="w-32 h-4 rounded-md bg-background-200 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((j) => (
              <div key={j} className="w-full h-5 rounded-md bg-background-200"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* ─── ERROR ─── */
  if (dataState === 'error') {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center max-w-sm mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-500 text-2xl"></i>
          </div>
          <h3 className="text-foreground-950 font-bold text-base mb-2">Error al cargar métricas de revenue</h3>
          <p className="text-foreground-500 text-sm mb-5">No se pudieron cargar los datos de monetización. Revisá tu conexión e intentá de nuevo.</p>
          <button onClick={retry} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-refresh-line"></i> Reintentar
          </button>
        </div>
      </div>
    );
  }

  /* ─── EMPTY ─── */
  if (dataState === 'empty') {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="text-center max-w-sm mx-auto px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-foreground-100 flex items-center justify-center">
            <i className="ri-money-dollar-circle-line text-3xl text-foreground-400"></i>
          </div>
          <h3 className="text-foreground-950 font-bold text-base mb-2">Sin datos de monetización</h3>
          <p className="text-foreground-500 text-sm mb-5">Todavía no hay ingresos ni campañas activas. Los datos aparecerán cuando empieces a generar revenue con tu radio.</p>
          <button onClick={retry} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap">
            <i className="ri-refresh-line"></i> Reintentar
          </button>
        </div>
      </div>
    );
  }

  /* ─── READY ─── */
  return (
    <div className="space-y-5">
      {/* Hero + Time Selector */}
      <div className="bg-background-50 rounded-2xl border border-background-200 p-5 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
              <i className="ri-money-dollar-circle-line text-xl text-amber-500"></i>
            </div>
            <div>
              <h3 className="text-foreground-950 font-extrabold text-lg">Monetización de tu Radio</h3>
              <p className="text-foreground-400 text-xs mt-0.5">
                Ingresos totales del período{' '}
                <strong className="text-foreground-700">{presetLabel.toLowerCase()}</strong>
                : <span className="text-amber-600 font-bold">${totalRevenue.toLocaleString()}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center bg-background-100 rounded-full p-1 shrink-0">
            {(Object.entries(monetizationTimePeriodLabels) as [MonetizationTimePeriod, string][]).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setTimePeriod(key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${
                  timePeriod === key
                    ? 'bg-background-50 text-foreground-950 shadow-sm'
                    : 'text-foreground-400 hover:text-foreground-600'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue KPI Cards */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center">
            <i className="ri-hand-coin-line text-emerald-500 text-xs"></i>
          </div>
          <h4 className="text-foreground-800 text-xs font-bold uppercase tracking-wide">Ingresos por Canal</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {revenueKpis.map((kpi) => (
            <MonetizationKpiCard key={kpi.id} kpi={kpi} timePeriod={timePeriod} />
          ))}
        </div>
      </div>

      {/* Performance KPI Cards */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-md bg-sky-50 flex items-center justify-center">
            <i className="ri-line-chart-line text-sky-500 text-xs"></i>
          </div>
          <h4 className="text-foreground-800 text-xs font-bold uppercase tracking-wide">Rendimiento Publicitario</h4>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {performanceKpis.map((kpi) => (
            <MonetizationKpiCard key={kpi.id} kpi={kpi} timePeriod={timePeriod} />
          ))}
        </div>
      </div>

      {/* Revenue Summary Bar */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
            <i className="ri-funds-line text-indigo-500 text-sm"></i>
          </div>
          <div>
            <h4 className="text-foreground-950 font-bold text-sm">Distribución de Ingresos</h4>
            <p className="text-foreground-400 text-[10px]">Cómo se reparte tu revenue en {presetLabel.toLowerCase()}</p>
          </div>
        </div>
        <div className="flex h-6 rounded-full overflow-hidden bg-background-100">
          {revenueKpis.map((kpi, idx) => {
            const pct = totalRevenue > 0 ? Math.round((kpi[timePeriod].value / totalRevenue) * 100) : 0;
            const barColors = ['bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-amber-500', 'bg-indigo-500'];
            return (
              <div
                key={kpi.id}
                className={`${barColors[idx]} h-full transition-all duration-700 ease-out`}
                style={{ width: `${pct}%`, minWidth: pct > 0 ? '2px' : '0px' }}
                title={`${kpi.label}: ${pct}%`}
              ></div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
          {revenueKpis.map((kpi, idx) => {
            const pct = totalRevenue > 0 ? Math.round((kpi[timePeriod].value / totalRevenue) * 100) : 0;
            const barColors = ['bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-amber-500', 'bg-indigo-500'];
            return (
              <div key={kpi.id} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-sm ${barColors[idx]} shrink-0`}></span>
                <span className="text-foreground-500 text-[10px]">{kpi.label}</span>
                <span className="text-foreground-700 text-[10px] font-semibold">{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tops Section */}
      {analyticsData && (
        <MonetizationTopsSection data={analyticsData.tops} activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      {/* Highlight cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <i className="ri-hand-coin-line text-emerald-500 text-lg"></i>
          </div>
          <div>
            <p className="text-foreground-400 text-[10px] font-medium">Mayor fuente propia</p>
            <p className="text-foreground-950 text-sm font-bold">
              Ingresos Propios (${revenueKpis[0][timePeriod].value.toLocaleString()})
            </p>
            <p className="text-foreground-400 text-[9px]">Publicidad directa + patrocinios</p>
          </div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
            <i className="ri-megaphone-line text-amber-500 text-lg"></i>
          </div>
          <div>
            <p className="text-foreground-400 text-[10px] font-medium">Mayor crecimiento</p>
            <p className="text-foreground-950 text-sm font-bold">Campañas (+26.1% anual)</p>
            <p className="text-foreground-400 text-[9px]">Revenue acumulado anual: $13,850</p>
          </div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0">
            <i className="ri-mouse-line text-sky-500 text-lg"></i>
          </div>
          <div>
            <p className="text-foreground-400 text-[10px] font-medium">CTR promedio</p>
            <p className="text-foreground-950 text-sm font-bold">
              {performanceKpis[0][timePeriod].value}%
            </p>
            <p className="text-foreground-400 text-[9px]">Click-through rate del período</p>
          </div>
        </div>
      </div>

      {/* Footer badge */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
              <i className="ri-money-dollar-circle-line text-amber-500 text-lg"></i>
            </div>
            <div>
              <p className="text-amber-800 text-xs font-semibold">Panel de Monetización</p>
              <p className="text-amber-600/80 text-[10px]">Métricas de revenue y rendimiento publicitario. Datos actualizados cada 30 minutos.</p>
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-full bg-amber-200 text-amber-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 w-fit">
            <i className="ri-time-line text-[9px]"></i>
            Actualizado ahora
          </span>
        </div>
      </div>
    </div>
  );
}